import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import API_URL from '../../config/api';

const PaymentSuccess = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [loginUrl, setLoginUrl] = useState('');
    const [error, setError] = useState('');
    const [demoData, setDemoData] = useState(null);
    const [demoMode, setDemoMode] = useState(false);
    const mountedRef = useRef(true);

    // Prefer router state (memory-only) for internal navigations; fall back
    // to query params for external payment callback redirects.
    const state = location.state || {};
    const txRef = state.tx_ref || searchParams.get('tx_ref') || searchParams.get('OrderMerchantReference') || searchParams.get('reference') || '';
    const orderTrackingId = state.order_tracking_id || searchParams.get('order_tracking_id') || searchParams.get('OrderTrackingId') || searchParams.get('transaction_id') || searchParams.get('session_id') || '';
    const isDemo = state.demo === 'true' || searchParams.get('demo') === 'true' || txRef.startsWith('DEMO-');
    const isSubscription = state.subscription === 'true' || searchParams.get('subscription') === 'true';
    const errorCode = searchParams.get('ErrorCode') || '';
    const errorMessage = searchParams.get('ErrorMessage') || searchParams.get('error') || '';
    const urlEmail = state.email || searchParams.get('email') || '';
    const stateLoginUrl = state.login_url || '';

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        // If the caller passed a login_url via router state, use it directly.
        if (stateLoginUrl) {
            setLoginUrl(stateLoginUrl);
        }
        const run = async () => {
            if (errorCode && errorCode !== '0') {
                navigate('/payment/failed?error=' + encodeURIComponent(errorMessage || 'Payment was declined. Please try again.'), { replace: true });
                return;
            }

            if (isSubscription) {
                // Subscription payments are activated by the charge-card/verify-charge
                // endpoints. Just show the success screen.
                setDemoMode(false);
                setLoading(false);
            } else if (isDemo || !orderTrackingId) {
                await handleDemoMode();
            } else {
                await handleRealPayment();
            }
        };
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [txRef, orderTrackingId, isDemo, errorCode]);

    const handleDemoMode = async () => {
        const signupDataStr = sessionStorage.getItem('signupData');
        let attempts = 0;
        const maxAttempts = 8;

        // Resolve the email for ownership verification.
        // Priority: URL param > signupData in sessionStorage
        let verifiedEmail = urlEmail;
        if (!verifiedEmail && signupDataStr) {
            try {
                verifiedEmail = JSON.parse(signupDataStr).email || '';
            } catch (e) { /* ignore */ }
        }

        while (mountedRef.current && attempts < maxAttempts) {
            try {
                const controller = new AbortController();
                const timeout = setTimeout(() => controller.abort(), 15000);
                const response = await fetch(`${API_URL}/api/payments/complete-demo`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ tx_ref: txRef, email: verifiedEmail }),
                    signal: controller.signal
                });
                clearTimeout(timeout);

                const data = await response.json();
                if (response.ok && data.success) {
                    setDemoMode(!!data.demo_mode);
                    setLoginUrl(data.login_url || '');
                    setDemoData(data.credentials && data.credentials.length ? { credentials: data.credentials } : null);
                    if (data.demo_mode === false) {
                        // Real-payment mode: the webhook will provision. Poll the
                        // by-email endpoint like the legacy flow did.
                        await pollDemoByEmail();
                        return;
                    }
                    setLoading(false);
                    sessionStorage.removeItem('signupData');
                    return;
                }
                if (response.status === 404) {
                    // Transaction not created yet (webhook still processing) — poll by-email
                    await pollDemoByEmail();
                    return;
                }
                // Demo mode disabled (real keys): webhook flow handles provisioning
                if (data.demo_mode === false) {
                    await pollDemoByEmail();
                    return;
                }
                setError(data.detail || 'Payment could not be completed. Please contact support.');
                setLoading(false);
                return;
            } catch (e) {
                if (e.name === 'AbortError') {
                    // Fall back to the webhook-poll path (real keys configured)
                    await pollDemoByEmail();
                    return;
                }
                attempts++;
                await new Promise(resolve => setTimeout(resolve, 3000));
            }
        }

        if (mountedRef.current) {
            if (signupDataStr) {
                await handleSignupLogin(signupDataStr);
            } else {
                setLoading(false);
            }
        }
    };

    // Webhook-poll path: after a real payment, the webhook
    // creates the demo; poll /api/demo/by-email until it appears.
    // Only poll if we actually have an auth token — without one the endpoint
    // returns 401 on every attempt, creating an infinite loop behind a
    // cheerful success screen.
    const pollDemoByEmail = async () => {
        const portalToken = localStorage.getItem('portalToken') || localStorage.getItem('token');
        if (!portalToken) {
            // No auth token — can't poll the protected endpoint. Show success
            // without credentials and direct the user to log in manually.
            setDemoMode(true);
            setLoading(false);
            return;
        }

        let attempts = 0;
        const maxAttempts = 10;

        while (mountedRef.current && attempts < maxAttempts) {
            try {
                const controller = new AbortController();
                const timeout = setTimeout(() => controller.abort(), 12000);
                const response = await fetch(`${API_URL}/api/demo/by-email?tx_ref=${txRef}`, {
                    headers: { 'Authorization': `Bearer ${portalToken}` },
                    signal: controller.signal
                });
                clearTimeout(timeout);
                if (response.status === 401) {
                    // Token invalid/expired — stop polling, show success without creds
                    setDemoMode(true);
                    setLoading(false);
                    return;
                }
                if (response.ok) {
                    const data = await response.json();
                    if (data.tenant_id) {
                        setDemoData(data);
                        setDemoMode(true);
                        setLoading(false);
                        return;
                    }
                }
            } catch (e) {
                // Not ready yet — keep polling
            }
            attempts++;
            await new Promise(resolve => setTimeout(resolve, 2000));
        }

        if (mountedRef.current) {
            // Still show success (payment was real) but without credentials
            setDemoMode(true);
            setLoading(false);
        }
    };

    // Attempt to generate a one-time login URL for the newly-registered
    // admin. This endpoint requires authentication — if the user doesn't
    // have a token yet (they just signed up), we skip it gracefully instead
    // of 401-looping.
    const handleSignupLogin = async (signupDataStr) => {
        try {
            const data = JSON.parse(signupDataStr);
            const token = localStorage.getItem('token') || localStorage.getItem('portalToken');
            if (!token) {
                // No auth token — can't call the OTLT endpoint. Tell the user
                // to log in manually with their registered credentials.
                setError('Your payment was successful! Please log in with the username and password you registered.');
                setLoading(false);
                return;
            }
            const response = await fetch(
                `${API_URL}/api/otlt/generate-login-url/${data.tenant_id}/${data.username}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setLoginUrl(result.login_url || '');
            } else if (response.status === 401) {
                setError('Your payment was successful! Please log in with the username and password you registered.');
            } else {
                setError('Your payment was successful, but we could not generate a login link. Please log in with the details you registered.');
            }
        } catch (e) {
            setError('Your payment was successful, but we could not generate a login link. Please log in with the details you registered.');
        } finally {
            sessionStorage.removeItem('signupData');
            setLoading(false);
        }
    };

    // Verify the payment status with the backend before declaring success.
    // The /api/payments/status/{ref} endpoint works for unauthenticated
    // callers (returns minimal status without PII). If the payment is not
    // confirmed, redirect to the failure page instead of showing a fake
    // success screen.
    const handleRealPayment = async () => {
        if (!txRef) {
            // No reference to verify — can't confirm payment
            navigate('/payment/failed?error=' + encodeURIComponent('Missing payment reference. If you believe this is an error, please contact support.'), { replace: true });
            return;
        }

        try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 15000);
            const response = await fetch(`${API_URL}/api/payments/status/${txRef}`, {
                signal: controller.signal
            });
            clearTimeout(timeout);

            if (!response.ok) {
                // Transaction not found or error — don't show fake success
                navigate('/payment/failed?error=' + encodeURIComponent('We could not verify your payment. If you were charged, please contact support with reference: ' + txRef), { replace: true });
                return;
            }

            const data = await response.json();
            if (data.status === 'successful') {
                // Payment confirmed — proceed with login URL generation
                const signupDataStr = sessionStorage.getItem('signupData');
                if (signupDataStr) {
                    await handleSignupLogin(signupDataStr);
                } else {
                    setLoading(false);
                }
            } else if (data.status === 'pending') {
                // Payment still processing — show a waiting state, not success
                setError('Your payment is still being processed. You will be notified once it completes. If you were charged, please contact support with reference: ' + txRef);
                setLoading(false);
            } else {
                // Payment failed/cancelled — redirect to failure page
                navigate('/payment/failed?error=' + encodeURIComponent('Payment was not completed (status: ' + data.status + '). Please try again.'), { replace: true });
            }
        } catch (e) {
            // Network error — can't verify, don't show fake success
            setError('We could not verify your payment due to a network error. If you were charged, please contact support with reference: ' + txRef);
            setLoading(false);
        }
    };

    const handleGoToApp = () => {
        if (loginUrl) {
            window.location.href = loginUrl;
        } else {
            navigate('/login');
        }
    };

    return (
        <div className="min-h-screen relative bg-gradient-to-br from-green-50 to-teal-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center px-4 overflow-hidden">
            <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-teal-400/20 dark:bg-teal-500/10 blur-3xl animate-blob pointer-events-none" aria-hidden="true"></div>
            <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-cyan-400/20 dark:bg-cyan-500/10 blur-3xl animate-blob animation-delay-2000 pointer-events-none" aria-hidden="true"></div>

            <div className="relative max-w-md w-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 dark:border-gray-700/50 p-8 animate-imageFadeIn">
                <div className="flex justify-center mb-6">
                    <div className="relative">
                        <div className="absolute inset-0 rounded-full bg-green-400/40 animate-ping" aria-hidden="true"></div>
                        <div className="relative rounded-full bg-gradient-to-br from-green-400 to-emerald-500 p-4 shadow-lg shadow-green-500/30">
                            <CheckCircleIcon className="h-14 w-14 text-white" />
                        </div>
                    </div>
                </div>

                <h1 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-2">
                    {isSubscription ? 'Subscription Activated!' : (demoMode ? 'Demo Access Activated!' : 'Welcome aboard!')}
                </h1>
                <p className="text-gray-600 dark:text-gray-400 text-center mb-8">
                    {isSubscription
                        ? 'Your subscription is now active. Your facility is live on Ospito+.'
                        : (demoMode
                            ? 'Your 7-day demo is ready. Use the credentials below to log in.'
                            : 'Your payment was successful and your facility is now live on Ospito+.')}
                </p>

                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                        <p className="text-sm text-red-600 dark:text-red-400 text-center">{error}</p>
                    </div>
                )}

                {demoMode && demoData && demoData.credentials && (
                    <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl p-4 mb-6">
                        <h3 className="font-semibold text-teal-900 dark:text-teal-200 mb-3">Your Demo Credentials</h3>
                        <div className="space-y-2">
                            {demoData.credentials.map((cred, i) => (
                                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-teal-100 dark:border-teal-900/30">
                                    <p className="text-xs font-bold text-teal-700 dark:text-teal-300">{cred.display_name || cred.role}</p>
                                    <div className="flex items-center justify-between mt-1">
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Username: <span className="font-mono font-semibold text-gray-900 dark:text-white">{cred.username}</span></p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Password: <span className="font-mono font-semibold text-gray-900 dark:text-white">{cred.password || cred.temporary_password}</span></p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(`${cred.username}\n${cred.password || cred.temporary_password}`).catch(() => {});
                                            }}
                                            className="text-[10px] px-2 py-1 rounded bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 hover:bg-teal-200 dark:hover:bg-teal-900/60 transition-colors"
                                        >
                                            Copy
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="text-[10px] text-teal-600 dark:text-teal-400 mt-3 text-center">
                            Demo expires in 7 days
                        </p>
                    </div>
                )}

                {demoMode && loading && (
                    <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl p-4 mb-6">
                        <p className="text-sm text-teal-700 dark:text-teal-300 text-center">
                            Preparing your demo credentials...
                        </p>
                    </div>
                )}

                {!demoMode && !error && !loading && (
                    <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl p-4 mb-6">
                        <h3 className="font-semibold text-teal-900 dark:text-teal-200 mb-2">You're all set — here's what's next:</h3>
                        <ul className="text-sm text-teal-800 dark:text-teal-300 space-y-1">
                            <li>✓ Your facility is now active</li>
                            <li>✓ Your admin account is ready</li>
                            <li>✓ You can start adding staff and patients</li>
                        </ul>
                    </div>
                )}

                <button
                    onClick={handleGoToApp}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold py-3 px-4 rounded-xl hover:shadow-lg hover:shadow-teal-500/30 hover:scale-[1.01] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {loading ? (
                        <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Preparing your account...
                        </span>
                    ) : (
                        'Go to Application'
                    )}
                </button>

                <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
                    Need help? Contact us at{' '}
                    <a href="mailto:ospitoplus@gmail.com" className="text-teal-600 dark:text-teal-400 hover:underline">
                        ospitoplus@gmail.com
                    </a>
                </p>
            </div>
        </div>
    );
};

export default PaymentSuccess;
